import { V3SynthMarketKey, ZERO_WEI } from '@kwenta/sdk/constants'
import {
	AssetKey,
	PerpsMarketV3,
	PerpsV3Trade,
	PositionSide,
	SupportedNetworkIds,
} from '@kwenta/sdk/types'
import {
	MarketKeyByAsset,
	calculateDesiredFillPrice,
	getDefaultPriceImpact,
} from '@kwenta/sdk/utils'
import { createSelector } from '@reduxjs/toolkit'
import Wei, { wei } from '@synthetixio/wei'
import {
	FuturesPositionTablePosition,
	FuturesPositionTablePositionWithDetails,
} from 'types/futures'

import { DEFAULT_DELAYED_CANCEL_BUFFER, MARGIN_ENGINE_ENABLED } from 'constants/defaults'
import { selectShowModal } from 'state/app/selectors'
import { selectSynthV3BalancesAndAllowances } from 'state/balances/selectors'
import { ZERO_STATE_MARGIN_INFO } from 'state/constants'
import { selectOffchainPricesInfo, selectPrices } from 'state/prices/selectors'
import { RootState } from 'state/store'
import { FetchStatus } from 'state/types'
import { selectProviderNetwork, selectWallet } from 'state/wallet/selectors'
import {
	unserializePerpsV3PositionHistory,
	unserializeTradeInputs,
	unserializeV3Market,
	unserializeV3Positions,
	unserializeV3Trades,
} from 'utils/futures'

import { unserializeCrossMarginTradePreview } from '../../../utils/futures'
import { selectMarketIndexPrice, selectMarketPriceInfo } from '../common/selectors'
import { MarkPriceInfos } from '../types'

import { MarkPrices, RiskStatus } from './types'

export const selectV3MarketKey = createSelector(
	(state: RootState) => state.crossMargin.selectedMarketAsset,
	(marketAsset) => MarketKeyByAsset[marketAsset]
)

export const selectV3Markets = createSelector(
	selectProviderNetwork,
	(state: RootState) => state.crossMargin,
	(network, crossMargin) => {
		const markets = crossMargin.markets[network] ? crossMargin.markets[network] : []
		return markets.map(unserializeV3Market)
	}
)

export const selectV3MarketInfo = createSelector(
	selectV3Markets,
	selectV3MarketKey,
	(markets, selectedMarket) => {
		return markets.find((market) => market.marketKey === selectedMarket)
	}
)

export const selectV3MarketId = createSelector(
	selectV3MarketInfo,
	(marketInfo) => marketInfo?.marketId
)

export const selectCrossMarginSupportedNetwork = (state: RootState) =>
	// TODO: Add support for mainnet
	state.wallet.signerNetworkId === SupportedNetworkIds.BASE_GOERLI

export const selectCrossMarginAccount = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.crossMargin,
	(wallet, network, crossMargin) => {
		return wallet ? crossMargin.accounts[network]?.[wallet]?.account : undefined
	}
)

export const selectAccountContext = createSelector(
	selectWallet,
	selectProviderNetwork,
	selectCrossMarginAccount,
	(wallet, network, accountId) => {
		return { wallet, network, accountId }
	}
)

export const selectCrossMarginAccountData = createSelector(
	selectWallet,
	selectProviderNetwork,
	selectCrossMarginSupportedNetwork,
	(state: RootState) => state.crossMargin,
	(wallet, network, supportedNetwork, crossMargin) => {
		return wallet && supportedNetwork ? crossMargin.accounts[network][wallet] : null
	}
)

export const selectMarginEnginePermitted = createSelector(
	selectCrossMarginAccountData,
	(account) => {
		return MARGIN_ENGINE_ENABLED && !!account?.marginEnginePermitted
	}
)

export const selectCrossMarginMarginInfo = createSelector(
	selectCrossMarginAccountData,
	(account) => {
		const {
			availableMargin,
			withdrawableMargin,
			requiredInitialMargin,
			requiredMaintenanceMargin,
		} = account ? account.marginInfo : ZERO_STATE_MARGIN_INFO

		return {
			availableMargin: wei(availableMargin).gt(0) ? wei(availableMargin) : wei(0),
			withdrawableMargin: wei(withdrawableMargin),
			requiredInitialMargin: wei(requiredInitialMargin),
			requiredMaintenanceMargin: wei(requiredMaintenanceMargin).gt(1)
				? wei(requiredMaintenanceMargin)
				: wei(0),
		}
	}
)

export const selectCrossMarginAvailableMargin = createSelector(
	selectCrossMarginMarginInfo,
	(marginInfo) => {
		return marginInfo.availableMargin
	}
)

export const selectMaintenanceMarginRatio = createSelector(
	selectCrossMarginMarginInfo,
	({ availableMargin, requiredMaintenanceMargin }) => {
		// Using > 1 as requiredMaintenanceMargin is always at least 1
		const ratio = requiredMaintenanceMargin.gt(1)
			? availableMargin.gt(0)
				? requiredMaintenanceMargin.div(availableMargin)
				: wei(1)
			: wei(0)
		const riskStatus = ratio.gt(0.5)
			? RiskStatus.High
			: ratio.gt(0.25)
			? RiskStatus.Moderate
			: RiskStatus.Low
		return { ratio, riskStatus }
	}
)

export const selectV3Spenders = createSelector(
	(state: RootState) => state.crossMargin,
	(crossMargin) => crossMargin.perpsV3Spenders
)

export const selectDepositAllowances = createSelector(
	selectV3Spenders,
	selectSynthV3BalancesAndAllowances,
	(spenders, balancesAndAllowances) => {
		if (!spenders) return {}
		return Object.keys(balancesAndAllowances).reduce<
			Partial<Record<V3SynthMarketKey, { marketProxy: Wei; marginEngine: Wei }>>
		>((acc, asset) => {
			const key = asset as V3SynthMarketKey
			acc[key] = {
				marketProxy: spenders.marketProxy
					? balancesAndAllowances[key]?.allowances[spenders.marketProxy] ?? wei(0)
					: wei(0),
				marginEngine: spenders.marginEngine
					? balancesAndAllowances[key]?.allowances[spenders.marginEngine] ?? wei(0)
					: wei(0),
			}
			return acc
		}, {})
	}
)

export const selectCrossMarginPositionHistory = createSelector(
	selectCrossMarginAccountData,
	(accountData) => {
		return unserializePerpsV3PositionHistory(accountData?.positionHistory ?? [])
	}
)

export const selectCrossMarginLiquidatedPositions = createSelector(
	selectCrossMarginPositionHistory,
	selectV3Markets,
	(history, markets) => {
		const liquidated = history.filter((h) => h.isLiquidated)
		return liquidated.reduce<FuturesPositionTablePositionWithDetails[]>((acc, l) => {
			const market = markets.find((m) => m.marketId === l.marketId)
			const newerPosition = history.find(
				(ph) => ph.marketId === l.marketId && ph.openTimestamp > l.openTimestamp
			)

			if (market && !newerPosition) {
				acc.push({
					...l,
					market,
					details: {
						...l,
						accruedFunding: wei(0),
						profitLoss: l.pnl,
						pnlPct: wei(0),
						canLiquidatePosition: false,
						liquidationPrice: wei(0),
						notionalValue: wei(0),
						isLiquidated: true,
						liquidatedAmount: l.liquidatedAmount,
						lastPrice: l.exitPrice ?? wei(0),
						initialMargin: wei(0),
						leverage: wei(0),
						marginRatio: wei(0),
						fundingIndex: 0,
						initialLeverage: wei(0),
						// TODO: Map position history from subgraph ata
						avgEntryPrice: l.avgEntryPrice,
					},
				})
			}
			return acc
		}, [])
	}
)

export const selectCrossMarginLeverageSide = (state: RootState) => state.crossMargin.leverageSide

export const selectCrossMarginTradeInputs = createSelector(
	selectCrossMarginLeverageSide,
	(state: RootState) => state.crossMargin.tradeInputs,
	(side, tradeInputs) => {
		const inputs = unserializeTradeInputs(tradeInputs)
		const deltas = {
			susdSizeDelta: side === PositionSide.LONG ? inputs.susdSize : inputs.susdSize.neg(),
			nativeSizeDelta: side === PositionSide.LONG ? inputs.nativeSize : inputs.nativeSize.neg(),
		}
		return {
			...inputs,
			...deltas,
			susdSizeString: tradeInputs.susdSize,
			nativeSizeString: tradeInputs.nativeSize,
		}
	}
)

type CrossMarginTrade = PerpsV3Trade & { market: PerpsMarketV3 }

export const selectCrossMarginMarginTransfers = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.crossMargin,
	(wallet, network, futures) => {
		if (!wallet) return []
		const account = futures.accounts[network]?.[wallet]
		return account?.marketMarginTransfers ?? []
	}
)

export const selectV3SynthPrices = createSelector(
	selectV3Markets,
	selectPrices,
	(markets, prices) => {
		return markets.reduce<Partial<Record<AssetKey, Wei>>>((acc, market) => {
			const price = prices[market.asset]?.offChain ?? wei(0)
			return {
				...acc,
				[market.asset]: price,
			}
		}, {})
	}
)

export const selectMarkPricesV3 = createSelector(
	selectV3Markets,
	selectPrices,
	(markets, prices) => {
		const markPrices: MarkPrices = {}
		return markets.reduce((acc, market) => {
			const price = prices[market.asset]?.offChain ?? wei(0)
			return {
				...acc,
				[market.marketKey]: wei(price).mul(
					wei(market.marketSkew).div(market.settings.skewScale).add(1)
				),
			}
		}, markPrices)
	}
)

export const selectMarkPriceInfosV3 = createSelector(
	selectV3Markets,
	selectOffchainPricesInfo,
	(markets, prices) => {
		const markPrices: MarkPriceInfos = {}
		return markets.reduce((acc, market) => {
			const price = prices[market.asset]?.price ?? wei(0)
			return {
				...acc,
				[market.marketKey]: {
					price: wei(price).mul(wei(market.marketSkew).div(market.settings.skewScale).add(1)),
					change: prices[market.asset]?.change ?? null,
				},
			}
		}, markPrices)
	}
)

export const selectCrossMarginPositions = createSelector(
	selectCrossMarginAccountData,
	selectV3Markets,
	selectMarkPricesV3,
	selectCrossMarginMarginInfo,
	selectCrossMarginPositionHistory,
	(
		account,
		markets,
		markPrices,
		{ availableMargin, requiredMaintenanceMargin },
		positionsHistory
	) => {
		const positions = unserializeV3Positions(account?.positions ?? [])

		return positions.reduce<FuturesPositionTablePosition[]>((acc, position) => {
			const market = markets.find((market) => market.marketId === position.marketId)

			const history = positionsHistory.find((ph) => {
				return ph.isOpen && ph.marketId === position.marketId
			})

			if (market) {
				const markPrice = markPrices[market?.marketKey!] ?? wei(0)

				const pnl = history
					? position.size.mul(
							history.avgEntryPrice.sub(markPrice).mul(position.side === PositionSide.LONG ? -1 : 1)
					  )
					: position.pnl

				const liqPrice = requiredMaintenanceMargin
					.sub(availableMargin)
					.div(position.side === PositionSide.LONG ? position.size : position.size.neg())
					.add(markPrice)

				acc.push({
					market: market,
					details: {
						...position,
						// TODO: Liquidation state
						pnl,
						canLiquidatePosition: false,
						liquidationPrice: liqPrice.lt(0) ? wei(0) : liqPrice,
						notionalValue: position.size.mul(markPrice),
						lastPrice: markPrice,
						initialMargin: wei(0),
						leverage: wei(0),
						marginRatio: wei(0),
						fundingIndex: 0,
						initialLeverage: wei(0),
						openTimestamp: history?.openTimestamp,
						// TODO: Map position history from subgraph ata
						avgEntryPrice: history?.avgEntryPrice,
					},
				})
			}
			return acc
		}, [])
	}
)

export const selectCrossMarginActivePositions = createSelector(
	selectCrossMarginPositions,
	(positions) => {
		return positions.filter((p) => !!p.details) as FuturesPositionTablePositionWithDetails[]
	}
)

export const selectSelectedCrossMarginPosition = createSelector(
	selectCrossMarginActivePositions,
	selectV3MarketInfo,
	(positions, market) => {
		const position = positions.find((p) => p.market.marketKey === market?.marketKey)
		return position
	}
)

export const selectAsyncCrossMarginOrder = createSelector(
	selectV3Markets,
	selectCrossMarginAccountData,
	(markets, account) => {
		const order = account?.pendingAsyncOrder
		if (!order) return

		const market = markets.find((m) => m.marketId === order.marketId)

		const strategy = market?.settlementStrategies.find(
			(s) => s.strategyId === order.settlementStrategyId
		)
		if (account && market && strategy) {
			const startTime = Number(order.settlementTime)
			const expirationTime = startTime + Number(strategy.settlementWindowDuration)
			const timePastExecution = Math.floor((Date.now() - startTime) / 1000)
			const executable = timePastExecution <= expirationTime
			const isStale =
				timePastExecution >
				DEFAULT_DELAYED_CANCEL_BUFFER + strategy.settlementWindowDuration.toNumber()

			const orderDetails = {
				market,
				account: Number(account.account),
				size: wei(order.sizeDelta),
				executableStartTime: startTime,
				expirationTime: expirationTime,
				marginDelta: wei(0),
				desiredFillPrice: wei(order.acceptablePrice),
				settlementWindowDuration: strategy.settlementWindowDuration.toNumber(),
				side: order.side,
				isStale: isStale,
				isExecutable: executable,
				settlementFee: strategy.settlementReward,
			}
			return orderDetails
		}
		return
	}
)

export const selectShowCrossMarginOnboard = (state: RootState) =>
	state.app.showModal?.type === 'futures_cross_margin_onboard'

export const selectWithdrawableCrossMargin = createSelector(
	selectCrossMarginMarginInfo,
	(marginInfo) => {
		return marginInfo.withdrawableMargin.gt(0) ? marginInfo.withdrawableMargin : wei(0)
	}
)

export const selectCrossMarginTradePreview = createSelector(
	selectCrossMarginTradeInputs,
	(state: RootState) => state.crossMargin.previews.trade,
	({ nativeSizeDelta }, preview) => {
		const priceImpact = getDefaultPriceImpact('market')
		const desiredFillPrice = calculateDesiredFillPrice(
			nativeSizeDelta,
			wei(preview?.fillPrice || 0),
			priceImpact
		)

		const unserialized = preview ? unserializeCrossMarginTradePreview(preview) : undefined
		return unserialized
			? {
					...unserialized,
					desiredFillPrice,
			  }
			: undefined
	}
)

// TODO: Improve max leverage calc for V3
export const selectCrossMarginMaxLeverage = createSelector(selectV3MarketInfo, (market) => {
	const leverageMultiplier = 3
	return market?.initialMarginRatio
		? wei(100).div(market?.initialMarginRatio).div(leverageMultiplier)
		: wei(25)
})

export const selectEditCMPositionModalMarketId = (state: RootState) =>
	state.app.showPositionModal?.marketKey

export const selectCloseCMPositionModalInfo = createSelector(
	selectEditCMPositionModalMarketId,
	selectCrossMarginActivePositions,
	selectV3Markets,
	selectPrices,
	(marketKey, crossPositions, markets, prices) => {
		const position = crossPositions.find(
			(p) => p.market.version === 3 && p.market.marketKey === marketKey
		)
		const market = markets.find((m) => m.marketKey === marketKey)
		if (!market) return { position: null, market: null, marketPrice: wei(0) }
		const price = prices[market.asset]
		// Note this assumes the order type is always delayed off chain
		return { position, market, marketPrice: price.offChain || wei(0) }
	}
)

export const selectCloseCMPositionOrderInputs = createSelector(
	(state: RootState) => state.crossMargin,
	(crossMargin) => {
		return crossMargin.closePositionOrderInputs
	}
)

export const selectCloseCMPositionPreview = createSelector(
	selectCloseCMPositionModalInfo,
	(state: RootState) => state.crossMargin,
	({ position, marketPrice }, crossMargin) => {
		const preview = crossMargin.previews.close
		const unserialized = preview ? unserializeCrossMarginTradePreview(preview) : null
		if (unserialized) {
			const priceImpact = getDefaultPriceImpact('market')
			const desiredFillPrice = calculateDesiredFillPrice(
				position?.details.side === PositionSide.LONG ? wei(-1) : wei(1),
				unserialized.fillPrice,
				priceImpact
			)

			return {
				...unserialized,
				price: marketPrice,
				desiredFillPrice,
				leverage: wei(0),
			}
		}
		return null
	}
)

export const selectPendingAsyncOrdersCount = createSelector(
	selectAsyncCrossMarginOrder,
	(order) => {
		return order ? 1 : 0
	}
)

export const selectActiveCrossMarginPositionsCount = createSelector(
	selectCrossMarginPositions,
	(positions) => {
		return positions.filter((p) => !!p).length
	}
)

export const selectV3SkewAdjustedPrice = createSelector(
	selectMarketIndexPrice,
	selectV3MarketInfo,
	(price, marketInfo) => {
		if (!marketInfo?.marketSkew || !marketInfo?.settings.skewScale) return price
		return price
			? wei(price).mul(wei(marketInfo.marketSkew).div(marketInfo.settings.skewScale).add(1))
			: ZERO_WEI
	}
)

export const selectV3SkewAdjustedPriceInfo = createSelector(
	selectMarketPriceInfo,
	selectV3MarketInfo,
	(priceInfo, marketInfo) => {
		if (!marketInfo?.marketSkew || !marketInfo?.settings.skewScale) return priceInfo
		return priceInfo
			? {
					price: wei(priceInfo.price).mul(
						wei(marketInfo.marketSkew).div(marketInfo.settings.skewScale).add(1)
					),
					change: priceInfo?.change,
			  }
			: undefined
	}
)

export const selectCollateralBalances = createSelector(selectCrossMarginAccountData, (account) => {
	return account?.collateralBalances ?? {}
})

export const selectCrossMarginAccountTrades = createSelector(
	selectCrossMarginAccountData,
	selectV3Markets,
	(account, markets) => {
		const trades = unserializeV3Trades(account?.trades ?? [])
		return trades.reduce<CrossMarginTrade[]>((acc, t) => {
			const market = markets.find((m) => m.marketId === t.marketId)
			if (market) {
				acc.push({
					...t,
					market: market,
				})
			}
			return acc
		}, [])
	}
)

export const selectGlobalTradesForMarket = createSelector(
	selectV3MarketId,
	selectProviderNetwork,
	(state: RootState) => state.crossMargin.tradesHistory,
	(marketId, network, tradesHistory) => {
		if (!marketId) return []
		return unserializeV3Trades(tradesHistory[network]?.[marketId] ?? [])
	}
)

export const selectIsV3MarketCapReached = createSelector(
	(state: RootState) => state.smartMargin.leverageSide,
	selectV3MarketInfo,
	selectMarketIndexPrice,
	(leverageSide, marketInfo, marketAssetRate) => {
		const maxMarketValueUSD = marketInfo?.marketLimitUsd ?? wei(0)
		const marketSize = marketInfo?.marketSize ?? wei(0)
		const marketSkew = marketInfo?.marketSkew ?? wei(0)

		return leverageSide === PositionSide.LONG
			? marketSize.add(marketSkew).div('2').abs().mul(marketAssetRate).gte(maxMarketValueUSD)
			: marketSize.sub(marketSkew).div('2').abs().mul(marketAssetRate).gte(maxMarketValueUSD)
	}
)

export const selectShowSwitchNetwork = createSelector(
	selectWallet,
	selectCrossMarginSupportedNetwork,
	selectShowModal,
	(walletAddress, supportedNetwork, openModal) => {
		return (
			walletAddress && !supportedNetwork && openModal !== 'futures_deposit_withdraw_smart_margin'
		)
	}
)

export const selectCrossMarginQueryStatuses = (state: RootState) => state.crossMargin.queryStatuses

export const fetchingGlobalTradeHistory = createSelector(
	(state: RootState) => state.crossMargin.queryStatuses,
	(statuses) => statuses.globalTradesHistory?.status === FetchStatus.Loading
)

export const selectIsFetchingCMTradePreview = createSelector(
	(state: RootState) => state.crossMargin,
	(crossMargin) => {
		return crossMargin.queryStatuses.tradePreview.status === FetchStatus.Loading
	}
)
